"use client";

import { useState, useEffect } from "react";
import { PredictorForm } from "@/components/predictor/PredictorForm";
import { PredictorResults } from "@/components/predictor/PredictorResults";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { HeroSection } from "@/components/shared/HeroSection";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart3, Sparkles, ArrowRight } from "lucide-react";
import { checkEligibility } from "@/lib/eligibility";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { fetchJsonData, validateCCMTData } from "@/lib/data-handlers";
import { AlertCircle } from "lucide-react";

export default function PredictorPage() {
  const [data, setData] = useState<any[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [isPredicting, setIsPredicting] = useState(false);
  const [hasPredicted, setHasPredicted] = useState(false);
  const [category, setCategory] = useState("");
  const [userScores, setUserScores] = useState<{ code: string; score: number }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState("2026_round1");
  const [predictionError, setPredictionError] = useState<string | null>(null);

  // Logic to handle prediction
  const handlePredict = async (predictionParams: {
    category: string;
    entries: { code: string; score: number }[];
    year: string;
  }) => {
    setIsPredicting(true);
    setPredictionError(null);
    setSelectedYear(predictionParams.year);
    setCategory(predictionParams.category);
    setUserScores(predictionParams.entries);

    try {
      // Load data for the selected year
      const result = await fetchJsonData(`/ccmt_${predictionParams.year}.json`);
      
      if (result.error) {
        setPredictionError(result.error);
        setResults([]);
        setHasPredicted(false);
        return;
      }

      if (!result.data) {
        setPredictionError('No data received from server');
        setResults([]);
        setHasPredicted(false);
        return;
      }

      const jsonData = result.data;
      const filtered = jsonData.filter((item: any) => {
        // 1. Match category
        if (item.category !== predictionParams.category) return false;

        // 2. Filter user entries that are eligible for this specific program
        const eligibleEntries = predictionParams.entries.filter(entry =>
          checkEligibility(item.program, [entry.code])
        );

        if (eligibleEntries.length === 0) return false;

        // 3. Get the max score among ELIGIBLE papers only
        const maxEligibleScore = Math.max(...eligibleEntries.map(e => e.score));

        // 4. Check if cutoff is satisfied
        return item.closingRank <= maxEligibleScore;
      });

      if (filtered.length === 0) {
        setPredictionError('No colleges found matching your criteria. Try adjusting your scores or category.');
      } else {
        setPredictionError(null);
      }

      setResults(filtered);
      setHasPredicted(true);

      // Smooth scroll to results
      setTimeout(() => {
        document.getElementById("results-section")?.scrollIntoView({ behavior: 'smooth' });
      }, 100);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setPredictionError(`Prediction failed: ${errorMessage}`);
      setResults([]);
      setHasPredicted(false);
      console.error("Prediction error:", error);
    } finally {
      setIsPredicting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <main className="container mx-auto px-4 py-8 sm:px-6 lg:py-16 max-w-full overflow-hidden">
        <HeroSection 
          title={<>Predict Your <span className="text-primary">GATE CCMT</span> <br className="hidden md:block" /> College & Program</>}
          description={<>Analyze your admission chances across <span className="text-on-surface font-bold">NITs, IIITs, and GFTIs</span> using multi-year historical cutoffs and CCMT's specialized group eligibility system.</>}
        />

        <div className="mt-0 flex flex-col lg:flex-row gap-8 items-start w-full overflow-hidden">
          {/* Left Side: Form (Fixed Width like Explorer Sidebar) */}
          <div className="w-full lg:w-80 lg:border-r border-outline-variant/10 lg:pr-8 lg:sticky lg:top-24">
            <PredictorForm onPredict={handlePredict} isLoading={isPredicting} />
          </div>

          {/* Right Side: Results or Placeholder */}
          <div className="flex-1 min-w-0 max-w-full" id="results-section">
            <AnimatePresence mode="wait">
              {predictionError && !hasPredicted && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center pt-20 min-h-[400px] border-2 border-destructive/20 rounded-[3rem] p-8 text-center bg-destructive/5"
                >
                  <div className="size-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                    <AlertCircle className="size-8 text-destructive" />
                  </div>
                  <h2 className="text-xl font-bold text-destructive mb-2">Prediction Failed</h2>
                  <p className="text-on-surface-variant max-w-sm mb-6">{predictionError}</p>
                  <Button 
                    onClick={() => setHasPredicted(false)} 
                    variant="secondary"
                    className="border border-outline-variant/20"
                  >
                    Try Again
                  </Button>
                </motion.div>
              )}
              {hasPredicted && !predictionError ? (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <PredictorResults
                    results={results}
                    userScores={userScores}
                    category={category}
                  />
                </motion.div>
              ) : !hasPredicted ? (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-start pt-20 h-full min-h-[400px] border-2 border-dashed border-outline-variant/20 rounded-[3rem] p-12 text-center bg-surface-container/10"
                >
                  <div className="size-20 rounded-[2rem] bg-primary/5 flex items-center justify-center mb-6">
                    <BarChart3 className="size-10 text-primary/40" />
                  </div>
                  <h2 className="text-2xl font-display font-black text-foreground mb-3">Ready to Predict?</h2>
                  <p className="text-on-surface-variant max-w-sm">
                    Enter your GATE scores and category in the form to see your eligible colleges and programs.
                  </p>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>

        {/* Static Editorial Content — Crawlable by Google */}
        <section className="mt-20 max-w-4xl mx-auto space-y-10 pb-10">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-display font-black text-foreground mb-3">How the CCMT College Predictor Works</h2>
            <p className="text-on-surface-variant text-sm md:text-base max-w-2xl mx-auto">A detailed explanation of the prediction algorithm, eligibility rules, and how to interpret your results for smart choice filling.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">The Prediction Algorithm Explained</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                The predictor compares your GATE score against 6 years of historical closing ranks (2021–2026) for every NIT, IIIT, and GFTI program. A program appears in your results if your score <strong className="text-foreground">meets or exceeds</strong> the historical closing rank for your selected year and reservation category. It does not account for seat availability in real-time — it is a data-driven reference tool based on verified past allotments to help you prioritize choices.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">GATE Paper Code Eligibility Groups</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                Not all GATE papers qualify for all M.Tech specializations. CCMT uses a <strong className="text-foreground">Group Eligibility System</strong> where each program belongs to a group that accepts candidates from specific GATE papers. For example, M.Tech in Computer Science may accept both CS and DA paper holders, while an M.Tech in Power Systems may only accept EE paper candidates. Our predictor automatically filters programs by your paper code eligibility, so every result you see is one you are genuinely eligible to apply for.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">Choosing the Right Prediction Year</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                The predictor defaults to the most recent available year. However, comparing results across multiple years is highly recommended. If a program shows up in your results for 2024 but not 2023, that means the cutoff dropped in 2024. Conversely, if it appeared in both 2023 and 2024, the program is consistently within your reach and is a reliable &quot;Target&quot; choice. Look for programs that appear across at least 2 years for the most reliable predictions.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">What the Results Do Not Tell You</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                The predictor shows historical eligibility — it does not guarantee a seat. Actual 2026 cutoffs depend on the total number of GATE qualified candidates, the GATE exam&apos;s difficulty, seat matrix revisions by institutes, and the collective choice preferences of candidates. Use this tool as a starting point for research, then cross-reference with the official CCMT cutoff brochure published by the organizing NIT each year.
              </p>
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-primary/5 border border-primary/20">
            <h3 className="font-bold text-lg text-foreground mb-3">Supported Categories & GATE Papers</h3>
            <p className="text-on-surface-variant text-sm leading-relaxed mb-4">
              The predictor supports all CCMT reservation categories: <strong className="text-foreground">OPEN, OBC-NCL, SC, ST, EWS</strong>, and all their respective PwD sub-categories. It supports all 29 GATE paper disciplines. For candidates with scores in multiple GATE papers (which CCMT allows), you can enter multiple paper codes and scores — the predictor will intelligently apply the correct eligible score for each program.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {["AE","AG","AR","BM","BT","CE","CH","CS","CY","DA","EC","EE","ES","GG","IN","MA","ME","MN","MT","NM","PE","PH","PI","ST","TF","XE","XH","XL","GE"].map((code) => (
                <span key={code} className="px-2 py-0.5 rounded bg-primary/5 border border-outline-variant/10 text-[10px] font-mono text-primary font-bold">{code}</span>
              ))}
            </div>
          </div>
        </section>

        {/* Permanent Explorer CTA Card (Downsized) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 bg-primary rounded-[2.5rem] p-8 md:p-10 text-primary-foreground relative overflow-hidden group shadow-2xl shadow-primary/20 border border-white/10"
        >
          <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] pointer-events-none" />
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/10 blur-[120px] rounded-full pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-[9px] font-black uppercase tracking-[0.2em]">
                <Sparkles className="size-3" />
                Detailed Insights
              </div>
              <h3 className="text-2xl md:text-3xl font-display font-black tracking-tight">Dive deeper into the data?</h3>
              <p className="text-primary-foreground/70 text-sm md:text-base font-medium max-w-xl">
                Explore the full dataset across 6 years with advanced filters, seat matrices, and institute-wise trends in our specialized explorer.
              </p>
            </div>

            <Link href="/explorer" className="shrink-0">
              <Button size="lg" className="bg-on-primary-fixed text-primary-fixed hover:scale-105 active:scale-95 h-14 px-8 rounded-xl text-lg font-black gap-3 transition-all shadow-xl shadow-black/10">
                Open Explorer
                <ArrowRight className="size-5" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
